<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { onClickOutside } from '@vueuse/core';
  import PosUiIcon from './PosUiIcon.vue';
  import { normalizeText } from '@/domain/pos/search-normalize';

  export interface IPosUiListboxItem {
    id: number | string;
    label: string;
  }

  const props = withDefaults(
    defineProps<{
      // Optional so Vue 2 skips runtime type-checks when null is passed
      // (a `null` entry in the compiled type array is not a valid constructor).
      value?: number | string | null;
      items: IPosUiListboxItem[];
      searchable?: boolean;
      placeholder?: string;
      disabled?: boolean;
      clearable?: boolean;
    }>(),
    {
      value: null,
      searchable: false,
      placeholder: undefined,
      disabled: false,
      clearable: false,
    }
  );

  const emit = defineEmits<{
    (e: 'input', value: number | string | null): void;
  }>();

  const rootRef = ref<HTMLElement>();
  const searchInputRef = ref<HTMLInputElement>();
  const open = ref(false);
  const search = ref('');

  const selectedItem = computed(
    () => props.items.find((it) => it.id === props.value) ?? null
  );

  const filteredItems = computed(() => {
    const q = normalizeText(search.value);
    if (!q) return props.items;
    return props.items.filter((it) => normalizeText(it.label).includes(q));
  });

  onClickOutside(rootRef, () => {
    open.value = false;
  });

  function toggle() {
    if (props.disabled) return;
    open.value = !open.value;
    if (open.value) {
      search.value = '';
      if (props.searchable) {
        setTimeout(() => searchInputRef.value?.focus(), 0);
      }
    }
  }

  function select(item: IPosUiListboxItem) {
    emit('input', item.id);
    open.value = false;
  }

  function clear(ev: Event) {
    ev.stopPropagation();
    emit('input', null);
    open.value = false;
  }
</script>

<template>
  <div
    ref="rootRef"
    class="pos-ui-listbox"
    :class="{ 'is-disabled': disabled }"
  >
    <button
      class="pos-ui-listbox-trigger"
      type="button"
      :disabled="disabled"
      @click="toggle"
    >
      <span
        class="pos-ui-listbox-trigger-label"
        :class="{ 'is-placeholder': !selectedItem }"
      >
        {{ selectedItem ? selectedItem.label : placeholder }}
      </span>
      <!-- span[role=button]: a real <button> can't be nested inside the trigger button -->
      <span
        v-if="clearable && selectedItem && !disabled"
        class="pos-ui-listbox-clear"
        role="button"
        tabindex="0"
        @click="clear"
        @keydown.enter.stop.prevent="clear"
      >
        <PosUiIcon name="close" />
      </span>
      <PosUiIcon
        name="chevron-down"
        class="pos-ui-listbox-chevron"
        :class="{ 'is-open': open }"
      />
    </button>

    <div v-if="open" class="pos-ui-listbox-panel">
      <div v-if="searchable" class="pos-ui-listbox-search">
        <PosUiIcon name="magnify" class="pos-ui-listbox-search-icon" />
        <input
          ref="searchInputRef"
          v-model="search"
          class="pos-ui-listbox-search-input"
          type="text"
          :placeholder="$t('posV2.search')"
        />
      </div>
      <ul class="pos-ui-listbox-options">
        <li v-if="!filteredItems.length" class="pos-ui-listbox-empty">
          {{ $t('posV2.noResults') }}
        </li>
        <li
          v-for="item in filteredItems"
          :key="item.id"
          class="pos-ui-listbox-option"
          :class="{ 'is-selected': item.id === value }"
          @click="select(item)"
        >
          <span class="pos-ui-listbox-option-label">{{ item.label }}</span>
          <PosUiIcon
            v-if="item.id === value"
            name="check"
            class="pos-ui-listbox-option-check"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-ui-listbox {
    @apply tw-relative tw-w-full;

    &.is-disabled {
      @apply tw-opacity-60;
    }

    .pos-ui-listbox-trigger {
      @apply tw-flex tw-h-11 tw-w-full tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-left tw-transition-colors;

      &:focus {
        @apply tw-border-orange-600 tw-outline-none tw-ring-1 tw-ring-orange-600;
      }

      &:disabled {
        @apply tw-cursor-not-allowed tw-bg-neutral-50;
      }

      .pos-ui-listbox-trigger-label {
        @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-neutral-700;

        &.is-placeholder {
          @apply tw-text-neutral-400;
        }
      }

      // Margins, not flex gap: old POS hardware (Chrome < 84).
      .pos-ui-listbox-clear {
        @apply tw-ml-1 tw-flex tw-cursor-pointer tw-items-center tw-text-neutral-400 hover:tw-text-neutral-600;
      }

      .pos-ui-listbox-chevron {
        @apply tw-ml-1 tw-text-lg tw-text-neutral-400 tw-transition-transform;

        &.is-open {
          @apply tw-rotate-180;
        }
      }
    }

    .pos-ui-listbox-panel {
      @apply tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-30 tw-mt-1 tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-surface tw-shadow-lg;

      .pos-ui-listbox-search {
        @apply tw-flex tw-items-center tw-border-b tw-border-neutral-100 tw-px-3;

        .pos-ui-listbox-search-icon {
          @apply tw-mr-2 tw-text-lg tw-text-neutral-400;
        }

        .pos-ui-listbox-search-input {
          @apply tw-h-11 tw-w-full tw-text-sm tw-text-neutral-700 tw-outline-none placeholder:tw-text-neutral-400;
        }
      }

      .pos-ui-listbox-options {
        @apply tw-m-0 tw-max-h-64 tw-list-none tw-overflow-y-auto tw-p-1;

        .pos-ui-listbox-empty {
          @apply tw-px-3 tw-py-3 tw-text-sm tw-text-neutral-500;
        }

        .pos-ui-listbox-option {
          @apply tw-flex tw-min-h-[44px] tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-md tw-px-3 tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

          &.is-selected {
            @apply tw-bg-orange-50 tw-font-medium tw-text-orange-700;
          }

          .pos-ui-listbox-option-label {
            @apply tw-min-w-0 tw-truncate;
          }

          .pos-ui-listbox-option-check {
            @apply tw-ml-2 tw-text-lg;
          }
        }
      }
    }
  }
</style>
